import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const PageContainer = styled.div`
  padding-top: 80px; /* Account for fixed header */
`;

const HeroSection = styled.section`
  background-color: var(--black);
  color: var(--white);
  padding: 6rem 0;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1.2rem;
  color: var(--gray-300);
  margin-bottom: 2rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ServicesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
`;

const ServiceSection = styled.section`
  margin-bottom: 6rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  scroll-margin-top: 100px; /* Adds margin for anchor links to account for fixed header */
  padding-top: 20px; /* Additional padding to ensure content is clearly visible */
  
  &:nth-child(even) {
    direction: rtl;
  }
  
  &:nth-child(even) > * {
    direction: ltr;
  }
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 2rem;
    
    &:nth-child(even) {
      direction: ltr;
    }
  }
`;

const ServiceContent = styled.div``;

const ServiceTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: var(--black);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const ServiceDescription = styled.p`
  font-size: 1.1rem;
  color: var(--gray-700);
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
`;

const ServiceFeature = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 1rem;
  font-size: 1.1rem;
  color: var(--gray-800);
`;

const FeatureIcon = styled.span`
  color: var(--primary);
  margin-right: 1rem;
  font-size: 1.5rem;
  line-height: 1;
`;

const ServiceImage = styled(motion.div)`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  
  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const CTASection = styled.section`
  background-color: var(--primary-light);
  padding: 5rem 0;
  text-align: center;
  border-radius: 8px;
  margin-bottom: 2rem;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  color: var(--black);
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: 2.5rem;
  color: var(--gray-700);
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(motion.button)`
  background-color: var(--primary);
  color: var(--black);
  border: none;
  border-radius: 4px;
  padding: 1rem 2.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: var(--primary-dark);
  }
`;

const ServicesPage = () => {
  useEffect(() => {
    // Get the hash from the URL (e.g., #architecture)
    const hash = window.location.hash;
    
    // If there's a hash, find the element and scroll to it
    if (hash) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If no hash, scroll to top
      window.scrollTo(0, 0);
    }
  }, []);
  
  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <PageTitle>Our Services</PageTitle>
          <PageDescription>
            Platform0 offers comprehensive architecture and AI solutions to help businesses innovate, scale, and transform in the digital age.
          </PageDescription>
        </HeroContent>
      </HeroSection>
      
      <ServicesContainer>
        <ServiceSection id="architecture">
          <ServiceContent>
            <ServiceTitle>Architecture as a Service</ServiceTitle>
            <ServiceDescription>
              Our Architecture as a Service offering provides businesses with access to expert architects who design, implement, and maintain scalable, resilient systems tailored to your specific needs.
            </ServiceDescription>
            <ServiceFeatures>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>System architecture design and optimization</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Microservices and distributed systems architecture</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Scalability and performance optimization</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Technical debt reduction and system modernization</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Architecture governance and best practices</span>
              </ServiceFeature>
            </ServiceFeatures>
          </ServiceContent>
          <ServiceImage
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img src={require('../assets/images/aaas.jpg')} alt="Architecture Services" />
          </ServiceImage>
        </ServiceSection>
        
        <ServiceSection id="ai-consultancy">
          <ServiceContent>
            <ServiceTitle>AI Consultancy</ServiceTitle>
            <ServiceDescription>
              Our AI consultancy helps businesses harness the power of artificial intelligence to drive innovation, efficiency, and competitive advantage through tailored AI solutions.
            </ServiceDescription>
            <ServiceFeatures>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>AI strategy development and roadmap planning</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Machine learning model development and deployment</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Natural language processing and computer vision solutions</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>AI-powered process automation and optimization</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>AI ethics, governance, and responsible AI implementation</span>
              </ServiceFeature>
            </ServiceFeatures>
          </ServiceContent>
          <ServiceImage
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img src={require('../assets/images/aiconsultant.png')} alt="AI Consultancy" />
          </ServiceImage>
        </ServiceSection>
        
        <ServiceSection id="cloud-solutions">
          <ServiceContent>
            <ServiceTitle>Cloud Solutions</ServiceTitle>
            <ServiceDescription>
              Our cloud solutions help businesses leverage the power of cloud computing to achieve scalability, flexibility, and cost-efficiency while maintaining security and performance.
            </ServiceDescription>
            <ServiceFeatures>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Cloud migration strategy and implementation</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Multi-cloud and hybrid cloud architecture</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Cloud-native application development</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Cloud security and compliance</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Cloud cost optimization and management</span>
              </ServiceFeature>
            </ServiceFeatures>
          </ServiceContent>
          <ServiceImage
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img src={require('../assets/images/cloud.png')} alt="Cloud Solutions" />
          </ServiceImage>
        </ServiceSection>
        
        <ServiceSection id="digital-transformation">
          <ServiceContent>
            <ServiceTitle>Digital Transformation</ServiceTitle>
            <ServiceDescription>
              Our digital transformation services help businesses reimagine their processes, systems, and customer experiences for the digital age, driving growth and innovation.
            </ServiceDescription>
            <ServiceFeatures>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Digital strategy development and roadmap planning</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Business process reengineering and optimization</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Legacy system modernization</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Digital customer experience design</span>
              </ServiceFeature>
              <ServiceFeature>
                <FeatureIcon>✓</FeatureIcon>
                <span>Data-driven decision making and analytics</span>
              </ServiceFeature>
            </ServiceFeatures>
          </ServiceContent>
          <ServiceImage
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <img src={require('../assets/images/transformation.png')} alt="Digital Transformation" />
          </ServiceImage>
        </ServiceSection>
        
        <CTASection>
          <CTAContent>
            <CTATitle>Ready to transform your business?</CTATitle>
            <CTADescription>
              Let's discuss how Platform0 can help you leverage architecture and AI to achieve your business goals.
            </CTADescription>
            <CTAButton
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Schedule a Consultation
            </CTAButton>
          </CTAContent>
        </CTASection>
      </ServicesContainer>
    </PageContainer>
  );
};

export default ServicesPage;
